import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiEntitlements } from "../api/entitlements";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ROLE_LABELS = { manager: "Manager", admin: "Admin", inspector: "Inspector", superadmin: "Super Admin", external_admin: "External Admin" };
const SOURCE_LABELS = { user: "user grant", "company+role": "company + role", company: "company", role: "role", global: "global", default: "no grant" };
const SOURCE_BADGE = {
  user: "tw-bg-indigo-100 tw-text-indigo-700",
  "company+role": "tw-bg-sky-100 tw-text-sky-700",
  company: "tw-bg-emerald-100 tw-text-emerald-700",
  role: "tw-bg-amber-100 tw-text-amber-700",
  global: "tw-bg-violet-100 tw-text-violet-700",
  default: "tw-bg-gray-100 tw-text-gray-600",
};

const inputCls = "tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-sky-300";
const labelCls = "tw-block tw-text-xs tw-font-semibold tw-text-gray-500 tw-mb-1";

function mutationOk(res) {
  if (res && res.data && typeof res.data.status !== "undefined") {return res.data.status === 200;}
  return !!res && res.status >= 200 && res.status < 300;
}

function Badge({ text, className }) {
  return <span className={`tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-whitespace-nowrap ${className}`}>{text}</span>;
}

function Spinner({ size = 20 }) {
  return <span className="tw-inline-block tw-animate-spin tw-rounded-full tw-border-2 tw-border-sky-300 tw-border-t-transparent" style={{ width: size, height: size }} />;
}

function Btn({ variant, children, ...rest }) {
  const base = "tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3.5 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed";
  const styles = variant === "primary" ? "tw-bg-sky-600 tw-text-white hover:tw-bg-sky-700"
    : variant === "danger" ? "tw-text-rose-600 hover:tw-bg-rose-50"
      : "tw-border tw-border-gray-300 tw-text-gray-700 tw-bg-white hover:tw-bg-gray-50";
  return <button {...rest} className={`${base} ${styles}`}>{children}</button>;
}

function Modal({ open, onClose, title, children, footer }) {
  if (!open) {return null;}
  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-px-4" style={{ background: "rgba(15,23,42,0.45)" }} onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={title} className="tw-w-full tw-max-w-lg tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-max-h-[85vh] tw-flex tw-flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-border-b tw-border-gray-100">
          <h3 className="tw-text-base tw-font-semibold tw-text-gray-800">{title}</h3>
          <button onClick={onClose} className="tw-text-gray-400 hover:tw-text-gray-700 tw-text-2xl tw-leading-none">&times;</button>
        </div>
        <div className="tw-px-6 tw-py-5 tw-overflow-y-auto">{children}</div>
        {footer && <div className="tw-flex tw-justify-end tw-gap-2 tw-px-6 tw-py-3 tw-border-t tw-border-gray-100 tw-bg-gray-50 tw-rounded-b-2xl">{footer}</div>}
      </div>
    </div>
  );
}

export default function EntitlementsAdmin() {
  const [keys, setKeys] = useState([]);
  const [grants, setGrants] = useState([]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadKeys = useCallback(() => {
    apiEntitlements.listKeys().then((r) => setKeys([...r.data.result || []].sort((a, b) => (a.name || a.key).localeCompare(b.name || b.key)))).catch(() => toast.error("Error loading feature keys"));
  }, []);
  const loadGrants = useCallback(() => {
    apiEntitlements.listGrants().then((r) => setGrants(r.data.result || [])).catch(() => toast.error("Error loading grants"));
  }, []);
  const loadUsers = useCallback(() => {
    apiEntitlements.listUsers().then((r) => setUsers(r.data.result || [])).catch(() => toast.error("Error loading users"));
  }, []);
  const loadCompanies = useCallback(() => {
    apiEntitlements.listCompanies().then((r) => setCompanies(r.data.result || [])).catch(() => toast.error("Error loading companies"));
  }, []);
  const loadWhoHas = useCallback((key) => {
    if (!key) { setMembers([]); return; }
    apiEntitlements.whoHas(key).then((r) => setMembers(r.data.result && r.data.result.members || [])).catch(() => setMembers([]));
  }, []);

  useEffect(() => { loadKeys(); loadGrants(); loadUsers(); loadCompanies(); }, [loadKeys, loadGrants, loadUsers, loadCompanies]);
  useEffect(() => { if (!selectedKey && keys.length) { setSelectedKey(keys[0].key); } }, [keys, selectedKey]);
  useEffect(() => { loadWhoHas(selectedKey); }, [selectedKey, loadWhoHas]);

  const keyGrants = grants.filter((g) => !selectedKey || g.featureKey === selectedKey);
  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const companyMap = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c])), [companies]);
  // Per-feature grant summary for the feature list: companies / roles / count of
  // separately-added users.
  const keySummary = useMemo(() => {
    const m = {};
    for (const g of grants) {
      const s = m[g.featureKey] || (m[g.featureKey] = { companies: new Set(), roles: new Set(), users: 0 });
      if (g.companyId > 0) {s.companies.add((companyMap[g.companyId] || {}).name || "#" + g.companyId);}
      if (g.role) {s.roles.add(ROLE_LABELS[g.role] || g.role);}
      if (g.userId > 0) {s.users++;}
    }
    const out = {};
    for (const k in m) {out[k] = { companies: [...m[k].companies], roles: [...m[k].roles], users: m[k].users };}
    return out;
  }, [grants, companyMap]);
  const keyName = (k) => { const f = keys.find((x) => x.key === k); return f && f.name || k; };

  const reload = () => { loadGrants(); loadWhoHas(selectedKey); };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this grant? Access it granted will be removed.")) {return;}
    apiEntitlements.deleteGrant(id).then((res) => {
      if (mutationOk(res)) { toast.success("Grant deleted"); reload(); }
      else {toast.error(res.data && res.data.message || "Failed to delete");}
    }).catch(() => toast.error("Failed to delete"));
  };

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (g) => { setEditing(g); setFormOpen(true); };

  return (
    <div className="tw-max-w-screen-xl tw-mx-auto tw-px-4 tw-py-6">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="tw-mb-6">
        <h1 className="tw-text-2xl tw-font-bold tw-text-gray-800">Entitlements</h1>
        <p className="tw-text-sm tw-text-gray-500">Control which companies, roles, and users can access each feature. (Rollout still uses feature flags.)</p>
      </div>

      <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden tw-mb-6">
        <table className="tw-min-w-full tw-divide-y tw-divide-gray-100 tw-text-sm">
          <thead><tr className="tw-bg-sky-50 tw-text-gray-500 tw-text-xs tw-uppercase tw-tracking-wider">
            <th className="tw-px-5 tw-py-3 tw-text-left tw-font-semibold">Feature</th>
            <th className="tw-px-5 tw-py-3 tw-text-left tw-font-semibold">Companies</th>
            <th className="tw-px-5 tw-py-3 tw-text-left tw-font-semibold">Roles</th>
            <th className="tw-px-5 tw-py-3 tw-text-left tw-font-semibold">Users</th>
          </tr></thead>
          <tbody className="tw-divide-y tw-divide-gray-50">
            {keys.length === 0 && <tr><td className="tw-px-5 tw-py-6 tw-text-gray-400" colSpan={4}>No feature keys registered.</td></tr>}
            {keys.map((k) => {
              const s = keySummary[k.key] || { companies: [], roles: [], users: 0 };
              const cell = (arr) => arr.length ? arr.join(", ") : "—";
              return (
                <tr key={k.key} onClick={() => setSelectedKey(k.key)} className={`tw-cursor-pointer hover:tw-bg-gray-50 ${selectedKey === k.key ? "tw-bg-sky-50" : ""}`}>
                  <td className="tw-px-5 tw-py-3"><div className="tw-font-medium tw-text-gray-800">{k.name}</div></td>
                  <td className="tw-px-5 tw-py-3 tw-text-gray-700">{cell(s.companies)}</td>
                  <td className="tw-px-5 tw-py-3 tw-text-gray-700">{cell(s.roles)}</td>
                  <td className="tw-px-5 tw-py-3 tw-text-gray-700">{s.users ? s.users + " user" + (s.users > 1 ? "s" : "") : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="tw-space-y-6">
        {!selectedKey ?
          <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-p-10 tw-text-center tw-text-gray-400">Select a feature above.</div>
          :
          <>
            <GrantsCard
              selectedName={keyName(selectedKey)} grants={keyGrants} userMap={userMap} companyMap={companyMap}
              onAdd={openCreate} onEdit={openEdit} onDelete={handleDelete}
            />
            <WhoHasCard members={members} selectedName={keyName(selectedKey)} />
          </>
        }
      </div>
      <GrantForm
        open={formOpen} onClose={() => setFormOpen(false)}
        editing={editing} selectedKey={selectedKey} featureName={keyName(editing ? editing.featureKey : selectedKey)} users={users} companies={companies}
        onSaved={() => { setFormOpen(false); reload(); toast.success("Grant saved"); }}
      />
    </div>
  );
}

function scopeLabel(g, userMap, companyMap) {
  if (g.userId > 0) {
    const u = userMap[g.userId];
    return u ? `User: ${u.name} (#${u.userId})` : `User #${g.userId}`;
  }
  if (g.companyId > 0 && g.role) {
    const c = companyMap[g.companyId];
    return `${c ? c.name : "Company #" + g.companyId} — ${ROLE_LABELS[g.role] || g.role}`;
  }
  if (g.companyId > 0) {
    const c = companyMap[g.companyId];
    return `${c ? c.name : "Company #" + g.companyId} (any role)`;
  }
  if (g.role) {return `Role: ${ROLE_LABELS[g.role] || g.role} (any company)`;}
  return "Everyone (global)";
}

function GrantsCard({ selectedName, grants, userMap, companyMap, onAdd, onEdit, onDelete }) {
  return (
    <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
      <div className="tw-flex tw-items-center tw-px-5 tw-py-4 tw-border-b tw-border-gray-100">
        <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800 tw-mr-auto">Grants — <span className="tw-text-sky-600">{selectedName}</span></h3>
        <Btn variant="primary" onClick={onAdd}>＋ Add grant</Btn>
      </div>
      {grants.length === 0 ? 
        <div className="tw-p-8 tw-text-center tw-text-gray-400">No grants — no one has this feature.</div>
        : 
        <table className="tw-min-w-full tw-divide-y tw-divide-gray-100 tw-text-sm">
          <thead><tr className="tw-bg-sky-50 tw-text-gray-500 tw-text-xs tw-uppercase tw-tracking-wider">
            <th className="tw-px-5 tw-py-3 tw-text-left tw-font-semibold">Scope</th>
            <th className="tw-px-5 tw-py-3 tw-text-left tw-font-semibold">Decision</th>
            <th className="tw-px-5 tw-py-3 tw-text-left tw-font-semibold">Note</th>
            <th className="tw-px-5 tw-py-3"></th>
          </tr></thead>
          <tbody className="tw-divide-y tw-divide-gray-50">
            {grants.map((g) => 
              <tr key={g.id} className="hover:tw-bg-gray-50">
                <td className="tw-px-5 tw-py-3 tw-text-gray-800">{scopeLabel(g, userMap, companyMap)}</td>
                <td className="tw-px-5 tw-py-3">{g.allowed ? <Badge text="Allow" className="tw-bg-emerald-100 tw-text-emerald-700" /> : <Badge text="Revoke" className="tw-bg-rose-100 tw-text-rose-700" />}</td>
                <td className="tw-px-5 tw-py-3 tw-text-gray-500">{g.note || "—"}</td>
                <td className="tw-px-5 tw-py-3 tw-whitespace-nowrap">
                  <button className="tw-text-sky-600 hover:tw-underline" onClick={() => onEdit(g)}>Edit</button>
                  <span className="tw-text-gray-300">|</span>
                  <button className="tw-text-rose-600 hover:tw-underline" onClick={() => onDelete(g.id)}>Delete</button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      }
    </div>
  );
}

function WhoHasCard({ members, selectedName }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q) {return members;}
    const s = q.toLowerCase();
    return members.filter((m) => (m.name || "").toLowerCase().includes(s) || (m.companyName || "").toLowerCase().includes(s) || String(m.userId).includes(s));
  }, [members, q]);
  return (
    <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
      <div className="tw-flex tw-items-center tw-px-5 tw-py-4 tw-border-b tw-border-gray-100">
        <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800 tw-mr-auto">Who has {selectedName} <span className="tw-text-gray-400 tw-font-normal">({members.length})</span></h3>
        <input className="tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-sky-300" placeholder="Search name / company…" value={q} onChange={(e) => setQ(e.target.value)} style={{ minWidth: 200 }} />
      </div>
      <div className="tw-max-h-[420px] tw-overflow-y-auto">
        {filtered.length === 0 ? 
          <div className="tw-p-8 tw-text-center tw-text-gray-400">No one currently has this feature.</div>
          : 
          <table className="tw-min-w-full tw-divide-y tw-divide-gray-100 tw-text-sm">
            <thead className="tw-sticky tw-top-0 tw-bg-gray-50"><tr className="tw-text-gray-500 tw-text-xs tw-uppercase tw-tracking-wider">
              <th className="tw-px-5 tw-py-2 tw-text-left tw-font-semibold">User</th>
              <th className="tw-px-5 tw-py-2 tw-text-left tw-font-semibold">Company</th>
              <th className="tw-px-5 tw-py-2 tw-text-left tw-font-semibold">Roles</th>
              <th className="tw-px-5 tw-py-2 tw-text-left tw-font-semibold">Because of</th>
            </tr></thead>
            <tbody className="tw-divide-y tw-divide-gray-50">
              {filtered.slice(0, 500).map((m) => 
                <tr key={m.userId} className="hover:tw-bg-gray-50">
                  <td className="tw-px-5 tw-py-2 tw-text-gray-800">{m.name} <span className="tw-text-gray-400">#{m.userId}</span></td>
                  <td className="tw-px-5 tw-py-2 tw-text-gray-600">{m.companyName || "—"}</td>
                  <td className="tw-px-5 tw-py-2 tw-text-gray-600">{m.roles || "—"}</td>
                  <td className="tw-px-5 tw-py-2"><Badge text={`${SOURCE_LABELS[m.source.kind] || m.source.kind}${m.source.grantId ? " #" + m.source.grantId : ""}`} className={SOURCE_BADGE[m.source.kind] || "tw-bg-gray-100"} /></td>
                </tr>
              )}
            </tbody>
          </table>
        }
        {filtered.length > 500 && <div className="tw-px-5 tw-py-3 tw-text-xs tw-text-gray-400 tw-bg-gray-50">Showing first 500 of {filtered.length}.</div>}
      </div>
    </div>
  );
}

const SCOPE_OPTIONS = [
  { value: "global", label: "Everyone (global)" },
  { value: "role", label: "A role (any company)" },
  { value: "company", label: "A whole company" },
  { value: "company+role", label: "A company + role" },
  { value: "user", label: "A specific user" },
];

function GrantForm({ open, onClose, editing, selectedKey, featureName, users, companies, onSaved }) {
  const [scope, setScope] = useState("global");
  const [role, setRole] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [userId, setUserId] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [allowed, setAllowed] = useState(true);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset only when the dialog opens or the edit target changes — NOT on every
  // users/companies refresh, so mid-entry form state isn't wiped.
  useEffect(() => {
    if (!open) {return;}
    if (editing) {
      let s = "global";
      if (editing.userId > 0) {s = "user";}
      else if (editing.companyId > 0 && editing.role) {s = "company+role";}
      else if (editing.companyId > 0) {s = "company";}
      else if (editing.role) {s = "role";}
      setScope(s);
      setRole(editing.role || "");
      setCompanyId(editing.companyId > 0 ? String(editing.companyId) : "");
      setUserId(editing.userId > 0 ? String(editing.userId) : "");
      const u = users.find((x) => x.id === editing.userId);
      setUserQuery(u ? `${u.name} (#${u.id})` : "");
      setAllowed(editing.allowed);
      setNote(editing.note || "");
    } else {
      setScope("global"); setRole(""); setCompanyId(""); setUserId(""); setUserQuery(""); setAllowed(true); setNote("");
    }
  }, [open, editing]);

  const userMatches = useMemo(() => {
    if (scope !== "user" || !userQuery) {return [];}
    const s = userQuery.toLowerCase();
    return users.filter((u) => (u.name || "").toLowerCase().includes(s) || String(u.id).includes(s) || (u.companyName || "").toLowerCase().includes(s)).slice(0, 30);
  }, [scope, userQuery, users]);

  const build = () => {
    const g = { featureKey: editing ? editing.featureKey : selectedKey, allowed, note };
    if (scope === "global") { g.userId = 0; g.companyId = 0; g.role = ""; }
    else if (scope === "role") { g.userId = 0; g.companyId = 0; g.role = role || "manager"; }
    else if (scope === "company") { g.userId = 0; g.companyId = Number(companyId) || 0; g.role = ""; }
    else if (scope === "company+role") { g.userId = 0; g.companyId = Number(companyId) || 0; g.role = role || "manager"; }
    else if (scope === "user") { g.userId = Number(userId) || 0; g.companyId = 0; g.role = ""; }
    return g;
  };

  const save = () => {
    const g = build();
    if ((scope === "company" || scope === "company+role") && !g.companyId) { toast.error("Pick a company."); return; }
    if (scope === "user" && !g.userId) { toast.error("Pick a user."); return; }
    setSaving(true);
    const done = (res) => {
      setSaving(false);
      if (res.data && res.data.status === 200) { onSaved(); }
      else { toast.error(res.data && (res.data.message || res.data.errors && res.data.errors[0] && res.data.errors[0].message) || "Failed to save"); }
    };
    (editing ? apiEntitlements.updateGrant(editing.id, g) : apiEntitlements.createGrant(g)).then(done).catch((e) => { setSaving(false); toast.error("Failed to save"); });
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? "Edit grant" : "Add grant"}
      footer={<>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={save} disabled={saving || !selectedKey && !editing}>{saving ? <Spinner /> : "Save grant"}</Btn>
      </>}>
      <div className="tw-space-y-4">
        <div>
          <label className={labelCls}>Feature</label>
          <div className="tw-text-sm tw-text-gray-800 tw-font-medium tw-px-3 tw-py-2 tw-bg-gray-50 tw-rounded-lg">{featureName}</div>
        </div>
        <div>
          <label className={labelCls}>Scope</label>
          <select className={inputCls} value={scope} onChange={(e) => setScope(e.target.value)}>
            {SCOPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {(scope === "role" || scope === "company+role") && 
          <div>
            <label className={labelCls}>Role</label>
            <select className={inputCls} value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="manager">Manager</option><option value="admin">Admin</option><option value="inspector">Inspector</option><option value="superadmin">Super Admin</option><option value="external_admin">External Admin</option>
            </select>
          </div>
        }
        {(scope === "company" || scope === "company+role") && 
          <div>
            <label className={labelCls}>Company</label>
            <select className={inputCls} value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              <option value="">— Select a company —</option>
              {companies.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </select>
          </div>
        }
        {scope === "user" && 
          <div>
            <label className={labelCls}>User (search by name / id / company)</label>
            <input className={inputCls} value={userQuery} onChange={(e) => { setUserQuery(e.target.value); setUserId(""); }} placeholder="Type to search…" />
            {userMatches.length > 0 && 
              <ul className="tw-mt-1 tw-border tw-border-gray-200 tw-rounded-lg tw-max-h-48 tw-overflow-y-auto tw-bg-white">
                {userMatches.map((u) => 
                  <li key={u.id} className="tw-px-3 tw-py-2 tw-text-sm hover:tw-bg-sky-50 tw-cursor-pointer tw-border-b tw-border-gray-50 last:tw-border-0"
                    onClick={() => { setUserId(String(u.id)); setUserQuery(`${u.name} (#${u.id})`); }}>
                    {u.name} <span className="tw-text-gray-400">#{u.id}</span> — {u.companyName}
                  </li>
                )}
              </ul>
            }
            {userId && <p className="tw-text-xs tw-text-emerald-600 tw-mt-1">Selected user #{userId}</p>}
          </div>
        }
        <div>
          <label className={labelCls}>Decision</label>
          <div className="tw-flex tw-gap-2">
            <Btn variant={allowed ? "primary" : ""} onClick={() => setAllowed(true)}>Allow</Btn>
            <Btn variant={!allowed ? "danger" : ""} onClick={() => setAllowed(false)}>Revoke</Btn>
          </div>
          <p className="tw-text-xs tw-text-gray-500 tw-mt-1">Revoke = deny as an exception, even if a broader grant would allow.</p>
        </div>
        <div>
          <label className={labelCls}>Note (optional)</label>
          <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this grant…" />
        </div>
      </div>
    </Modal>
  );
}